// Utility helpers for ZoomPanDraggable

export function normalizeAngle(value) {
  const raw = Number.isFinite(value) ? value : 0
  const normalized = raw % 360
  return normalized < 0 ? normalized + 360 : normalized
}

const RAD_PER_DEG = Math.PI / 180

function degreesToRadians(value) {
  return (Number.isFinite(value) ? value : 0) * RAD_PER_DEG
}

export function getRotationMatrix(degrees) {
  const radians = degreesToRadians(degrees)
  return {
    cos: Math.cos(radians),
    sin: Math.sin(radians),
  }
}

export function rotatePointWithMatrix(point, matrix) {
  const { x = 0, y = 0 } = point || {}
  const cos = matrix?.cos ?? 1
  const sin = matrix?.sin ?? 0

  return {
    x: x * cos - y * sin,
    y: x * sin + y * cos,
  }
}

export function rotatePoint(point, degrees) {
  return rotatePointWithMatrix(point, getRotationMatrix(degrees))
}

export function computeRotationFromClient(element, clientX, clientY) {
  if (!element) return null
  const rect = element.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2
  const angleRadians = Math.atan2(clientY - centerY, clientX - centerX)
  const angleDegrees = (angleRadians * 180) / Math.PI + 90
  return normalizeAngle(angleDegrees)
}

export function getBounds({ controller, pageRect, stageRef, minSize, stageRectToViewRectFn }) {
  let boundary
  if (controller && pageRect) {
    boundary = stageRectToViewRectFn(controller, pageRect)
  }
  if (!(boundary?.width && boundary?.height)) {
    const fallbackRect = stageRef?.current?.getBoundingClientRect()
    if (fallbackRect) {
      boundary = {
        height: fallbackRect.height,
        width: fallbackRect.width,
        x: fallbackRect.left,
        y: fallbackRect.top,
      }
    } else if (typeof window !== 'undefined') {
      boundary = {
        height: window.innerHeight,
        width: window.innerWidth,
        x: 0,
        y: 0,
      }
    }
  }
  return {
    boundary,
    minHeight: minSize,
    minWidth: minSize,
  }
}

// --- Coordinate Utils ---

const DEFAULT_OFFSET = Object.freeze({ x: 0, y: 0 })

export function addStageOffset(stageRect, offset = DEFAULT_OFFSET) {
  return {
    ...stageRect,
    x: stageRect.x + (offset.x ?? 0),
    y: stageRect.y + (offset.y ?? 0),
  }
}

export function subtractStageOffset(stageRect, offset = DEFAULT_OFFSET) {
  return {
    ...stageRect,
    x: stageRect.x - (offset.x ?? 0),
    y: stageRect.y - (offset.y ?? 0),
  }
}

export function rectsAreEqual(a, b, epsilon = 0.25) {
  if (!a || !b) return false

  return (
    Math.abs(a.height - b.height) <= epsilon &&
    Math.abs(a.width - b.width) <= epsilon &&
    Math.abs(a.x - b.x) <= epsilon &&
    Math.abs(a.y - b.y) <= epsilon
  )
}

const EMPTY_RECT = Object.freeze({ height: 0, width: 0, x: 0, y: 0 })

function normalizeRect(rect) {
  if (!rect) return { ...EMPTY_RECT }

  const width = rect.width ?? 0
  const height = rect.height ?? 0

  return {
    height,
    width,
    x: rect.x ?? 0,
    y: rect.y ?? 0,
  }
}

function computeRectFromPoints(topLeft, bottomRight) {
  if (!topLeft || !bottomRight) {
    return { ...EMPTY_RECT }
  }

  const [x1, y1] = topLeft
  const [x2, y2] = bottomRight

  return {
    height: y2 - y1,
    width: x2 - x1,
    x: x1,
    y: y1,
  }
}

export function stageRectToViewRect(controller, stageRect) {
  const rect = normalizeRect(stageRect)

  if (!controller) {
    return rect
  }

  if (typeof controller.rectFromStageToView === 'function') {
    const result = controller.rectFromStageToView(rect, false)
    if (result) {
      return normalizeRect(result)
    }
  }

  if (typeof controller.fromStageToView === 'function') {
    return computeRectFromPoints(
      controller.fromStageToView(rect.x, rect.y, false, true),
      controller.fromStageToView(rect.x + rect.width, rect.y + rect.height, false, true)
    )
  }

  return rect
}

export function viewRectToStageRect(controller, viewRect) {
  const rect = normalizeRect(viewRect)

  if (!controller) {
    return rect
  }

  if (typeof controller.rectFromViewToStage === 'function') {
    const result = controller.rectFromViewToStage(rect, false)
    if (result) {
      return normalizeRect(result)
    }
  }

  if (typeof controller.fromViewToStage === 'function') {
    return computeRectFromPoints(
      controller.fromViewToStage(rect.x, rect.y, false, true),
      controller.fromViewToStage(rect.x + rect.width, rect.y + rect.height, false, true)
    )
  }

  return rect
}

export function measureStageRect({ controller, element, getBoundingRect }) {
  if (!controller || !element) {
    return null
  }

  if (typeof getBoundingRect === 'function') {
    const rect = getBoundingRect(element)
    if (rect) {
      return rect
    }
  }

  if (typeof element.getBoundingClientRect !== 'function') {
    return null
  }

  const viewRect = element.getBoundingClientRect()

  return viewRectToStageRect(controller, {
    height: viewRect.height,
    width: viewRect.width,
    x: viewRect.left,
    y: viewRect.top,
  })
}
